/**
 * OrchestrateHQ AI Frontend Main Application
 *
 * Purpose: React application entry point with routing, theme configuration,
 *          and authentication context for the OrchestrateHQ AI platform.
 *
 * Architecture: Main application component that sets up routing, theming,
 *              and global state management for the frontend.
 *
 * Dependencies: React, React Router, Material-UI, AuthContext, Google OAuth
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-08-06
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LicenseInfo } from '@mui/x-license';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocaleProvider } from './context/LocaleContext';
import { ConnectivityProvider } from './context/ConnectivityContext';
import { SidebarProvider } from './context/SidebarContext';
import { NavigationGuardProvider } from './context/NavigationGuardContext';
import PlatformStatusBanner from './components/common/PlatformStatusBanner';
import LoginPage from './pages/Login/LoginPage';
import AcceptInvitePage from './pages/AcceptInvite/AcceptInvitePage';
import ResetPasswordPage from './pages/ResetPassword/ResetPasswordPage';
import HomePage from './pages/Home/HomePage';
import PlanningPage from './pages/Planning/PlanningPage';
import CreateMediaPlanPage from './pages/Planning/CreateMediaPlanPage';
import ProtectedRoute from './components/ProtectedRoute';
import { ModuleKey, PermissionKey } from './constants/permissionKeys';
import AdminPage from './pages/Admin/AdminPage';
import ComponentLibraryDemo from './pages/Admin/UIDemo';
import MetadataManagerPage from './pages/Admin/MetadataManagerPage';
import MDMManagerPage from './pages/Admin/MDMManagerPage';
import RoleManagerPage from './pages/Admin/RoleManager';
import RoleEditorPage from './pages/Admin/RoleManager/RoleEditorPage';
import UserManagementPage from './pages/Admin/UserManagement/UserManagementPage';
import UserPage from './pages/Admin/UserManagement/UserPage';
import UserProfilePage from './pages/Profile/UserProfilePage';
import WorkflowManagerPage from './pages/Admin/WorkflowManagerPage';
import AIAgentManagerPage from './pages/Admin/AIAgentManagerPage';
import AISkillsManagerPage from './pages/Admin/AISkillsManagerPage';
import orchestrateTheme from './theme/orchestrateTheme';
import { useMDMPreload } from './services/mdmCacheService';

// Lazy-loaded components for code splitting
const TenantManagerPage = lazy(() => import('./pages/Admin/TenantManager'));

/**
 * Loading fallback for lazy-loaded components
 *
 * Purpose: Display loading state while lazy components are being loaded
 */
const PageLoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Expose QueryClient to window for debugging in development
if (import.meta.env.DEV) {
  (window as any).__REACT_QUERY_CLIENT__ = queryClient;
}

// Environment configuration
const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  '110640494571-lqkqdc5d8lsmn3r1a5o0sllt513t93ll.apps.googleusercontent.com';

// Configure MUI Pro license key
const MUI_LICENSE_KEY = import.meta.env.VITE_MUI_LICENSE_KEY;
if (MUI_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);
}

/**
 * Note: Theme configuration has been moved to ./theme/orchestrateTheme.ts
 * This centralizes all brand identity, colors, typography, and component overrides
 * following the Orchestrate Brand Identity document.
 */

/**
 * Global CSS styles for the application
 *
 * Purpose: Ensures consistent base styling across all components
 * Logic: Uses Material-UI GlobalStyles component with CSS reset and base styles
 */
const globalStyles = (
  <GlobalStyles
    styles={{
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        height: '100%',
      },
      body: {
        height: '100%',
        margin: 0,
        padding: 0,
        fontFamily: orchestrateTheme.typography.fontFamily,
      },
      '#root': {
        height: '100%',
      },
    }}
  />
);

/**
 * 404 Not Found component
 *
 * Purpose: Displays a user-friendly error page for non-existent routes
 * Logic: Renders a centered error message with navigation link
 * Input: None
 * Output: JSX element with error message and navigation
 *
 * @returns JSX element for 404 page
 */
const NotFoundPage: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      textAlign: 'center',
      gap: '1rem',
    }}
  >
    <h1>404 - Page Not Found</h1>
    <p>The page you're looking for doesn't exist.</p>
    <a href="/home">Go to Home</a>
  </div>
);

/**
 * MDM Preload Provider
 *
 * Purpose: Preloads common MDM fields (currencies) at app startup for faster lookups
 * Logic: Uses useMDMPreload hook with current tenant from auth context
 * Input: children - React nodes to render
 * Output: Children wrapped with MDM preloading side effect
 */
const MDMPreloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const currentTenantId = state.user?.activeTenant?.tenant_id;

  // Preload common MDM fields (currencies) for the current tenant
  useMDMPreload(currentTenantId);

  return <>{children}</>;
};

/**
 * Main application component
 *
 * Purpose: Renders the complete application with routing, theming, and authentication
 * Logic: Sets up providers, routing, and global state management with Orchestrate brand theme
 * Input: None
 * Output: Complete React application
 *
 * @returns JSX element representing the complete application
 */
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={orchestrateTheme}>
        <CssBaseline />
        {globalStyles}
        <ConnectivityProvider>
          <PlatformStatusBanner />
          <AuthProvider>
            <MDMPreloadProvider>
              <SidebarProvider>
                <Router>
                  {/* ORCH-191: formats everything below in the signed-in user's locale */}
                  <LocaleProvider>
                    <NavigationGuardProvider>
                      <Routes>
                        {/* Public routes - GoogleOAuthProvider only wraps LoginPage to avoid FedCM errors on other pages */}
                        <Route
                          path="/login"
                          element={
                            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                              <LoginPage />
                            </GoogleOAuthProvider>
                          }
                        />
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/login/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />

                        {/* Protected routes */}
                        <Route
                          path="/home"
                          element={
                            <ProtectedRoute>
                              <HomePage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Redirect old dashboard route */}
                        <Route path="/dashboard" element={<Navigate to="/home" replace />} />

                        {/* Planning routes */}
                        <Route
                          path="/planning"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.PLANNING}>
                              <PlanningPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/planning/create"
                          element={
                            <ProtectedRoute
                              requiredModule={ModuleKey.PLANNING}
                              requiredPermissions={[PermissionKey.MEDIAPLAN_CREATE]}
                            >
                              <CreateMediaPlanPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Admin routes */}
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN}>
                              <AdminPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Internal tool: open to every GLOBAL member, no module (ORCH-415) */}
                        <Route
                          path="/admin/component-library"
                          element={
                            <ProtectedRoute globalOnly>
                              <ComponentLibraryDemo />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/metadata-manager"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_METADATA_MANAGER}>
                              <MetadataManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/role-manager/new"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_ROLE_MANAGER}>
                              <RoleEditorPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/role-manager/:roleId"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_ROLE_MANAGER}>
                              <RoleEditorPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/role-manager"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_ROLE_MANAGER}>
                              <RoleManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/mdm"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_MDM_MANAGER}>
                              <MDMManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/workflow-manager"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_WORKFLOW_MANAGER}>
                              <WorkflowManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/ai-agent-manager"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_AI_AGENT_MANAGER}>
                              <AIAgentManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/ai-skills"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_AI_SKILLS_MANAGER}>
                              <AISkillsManagerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/tenant-manager"
                          element={
                            <ProtectedRoute
                              requiredModule={ModuleKey.ADMIN_TENANT_MANAGER}
                              globalOnly
                            >
                              <Suspense fallback={<PageLoadingFallback />}>
                                <TenantManagerPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/user-management/:userId"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_USER_MANAGER}>
                              <UserPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/user-management"
                          element={
                            <ProtectedRoute requiredModule={ModuleKey.ADMIN_USER_MANAGER}>
                              <UserManagementPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* User profile (self-service, any authenticated user) */}
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <UserProfilePage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Default redirect */}
                        <Route path="/" element={<Navigate to="/home" replace />} />

                        {/* 404 fallback */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </NavigationGuardProvider>
                  </LocaleProvider>
                </Router>
              </SidebarProvider>
            </MDMPreloadProvider>
          </AuthProvider>
        </ConnectivityProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
